import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Paper,
  Typography,
  Divider,
  Chip,
  Collapse,
  InputAdornment,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import PersonIcon from "@mui/icons-material/Person";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ClientSelectionDialog from "./ClientSelectionDialog";

const PaymentFilters = ({
  activeTab,
  dateRange,
  setDateRange,
  selectedClient,
  setSelectedClient,
  clients,
  onResetFilters,
  colors,
}) => {
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const getDateLabel = () => {
    switch (activeTab) {
      case 0:
        return "Date d'échéance";
      case 1:
        return "Date de paiement";
      case 2:
        return "Date d'échéance";
      default:
        return "Date";
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setClientDialogOpen(false);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedClient) count++;
    if (dateRange.start || dateRange.end) count++;
    return count;
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        mb: 2,
        bgcolor: colors.primary[900],
        borderRadius: "12px",
        border: `1px solid ${colors.grey[700]}`,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilterAltIcon sx={{ color: colors.grey[100] }} />
            <Typography variant="h6" color={colors.grey[100]}>
              Filtres
            </Typography>
            {getActiveFiltersCount() > 0 && (
              <Chip
                label={`${getActiveFiltersCount()} actif${
                  getActiveFiltersCount() > 1 ? "s" : ""
                }`}
                size="small"
                sx={{
                  bgcolor: colors.greenAccent[600],
                  color: colors.grey[100],
                }}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={1}>
            <Tooltip title={expanded ? "Réduire" : "Développer"}>
              <IconButton
                onClick={() => setExpanded(!expanded)}
                size="small"
                sx={{ color: colors.grey[100] }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
            {getActiveFiltersCount() > 0 && (
              <Tooltip title="Réinitialiser les filtres">
                <IconButton
                  onClick={onResetFilters}
                  size="small"
                  sx={{
                    bgcolor: colors.blueAccent[700],
                    "&:hover": { bgcolor: colors.blueAccent[800] },
                    color: colors.grey[100],
                  }}
                >
                  <FilterAltOffIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Box>

      <Collapse in={expanded}>
        <Divider sx={{ my: 2, borderColor: colors.grey[700] }} />

        <Stack spacing={3}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="flex-start"
          >
            <Box sx={{ flex: 1, width: { xs: "100%", md: "auto" } }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setClientDialogOpen(true)}
                startIcon={<PersonIcon />}
                sx={{
                  height: "56px",
                  justifyContent: "flex-start",
                  borderColor: colors.grey[700],
                  color: selectedClient ? colors.grey[100] : colors.grey[400],
                  "&:hover": {
                    borderColor: colors.grey[500],
                    bgcolor: `${colors.primary[600]} !important`,
                  },
                }}
              >
                {selectedClient
                  ? `${selectedClient.nom} ${selectedClient.prenom}`
                  : "Sélectionner un client"}
              </Button>
            </Box>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{ flex: 2, width: { xs: "100%", md: "auto" } }}
            >
              <TextField
                fullWidth
                label={`${getDateLabel()} (début)`}
                type="date"
                value={dateRange.start}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, start: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateRangeIcon sx={{ color: colors.grey[400] }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: colors.grey[700],
                    },
                    "&:hover fieldset": {
                      borderColor: colors.grey[500],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.blueAccent[500],
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: colors.grey[400],
                  },
                  "& .MuiInputBase-input": {
                    color: colors.grey[100],
                  },
                }}
              />

              <TextField
                fullWidth
                label={`${getDateLabel()} (fin)`}
                type="date"
                value={dateRange.end}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, end: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateRangeIcon sx={{ color: colors.grey[400] }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: colors.grey[700],
                    },
                    "&:hover fieldset": {
                      borderColor: colors.grey[500],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.blueAccent[500],
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: colors.grey[400],
                  },
                  "& .MuiInputBase-input": {
                    color: colors.grey[100],
                  },
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </Collapse>

      <ClientSelectionDialog
        open={clientDialogOpen}
        onClose={() => setClientDialogOpen(false)}
        onSelect={handleClientSelect}
        clients={clients}
        onClientAdded={null}
      />
    </Paper>
  );
};

export default PaymentFilters;
